import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Badge, Button, Dialog, EmptyState, Field, Input, Section, Select, Spinner, Switch } from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import { useStoreChoice } from '../lib/store-choice';
import type { SubscriptionList, SubscriptionRuns, SubscriptionSettingsView } from '../lib/types';

/**
 * Satış → Düzenli siparişler (F7-19, ADR-124): mağaza başına ayar (varsayılan kapalı; aralıklar, indirim, ödeme
 * bağlantısı süresi), hazırlık durumu ve abonelik listesi (dönem kayıtları, gerekçeyle iptal).
 */

const WEEKS = [1, 2, 3, 4, 6, 8, 10, 12];
const HOURS = [24, 48, 72, 96, 120, 168];
type Settings = SubscriptionSettingsView['settings'];
type Row = SubscriptionList['items'][number];

export function SubscriptionsPage() {
  const t = useT();
  const { stores, storeId, setStoreId } = useStoreChoice();
  return (
    <PageFrame helpKey="page.subscriptions">
      <div className="space-y-8">
        <p className="max-w-[72ch] text-sm text-ink-2">{t('admin.subscriptions.lead')}</p>
        {stores.length > 1 && (
          <Field label={t('admin.subscriptions.store')} className="max-w-xs">
            {(p) => (
              <Select {...p} value={storeId} onChange={(e) => setStoreId(e.target.value)}>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}
        {storeId && <SettingsSection key={storeId} storeId={storeId} />}
        {storeId && <ListSection storeId={storeId} />}
      </div>
    </PageFrame>
  );
}

function SettingsSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { can } = usePermissions();
  const canManage = can('org.settings.update');
  const path = `/v1/admin/stores/${storeId}/subscription-settings`;
  const q = useQuery({
    queryKey: ['subscription-settings', storeId],
    queryFn: () => api.get<SubscriptionSettingsView>(path),
  });
  const [draft, setDraft] = useState<Settings | null>(null);
  useEffect(() => {
    if (q.data) setDraft(q.data.settings);
  }, [q.data]);
  const save = useApiMutation(() => api.put<SubscriptionSettingsView>(path, draft), {
    invalidate: [['subscription-settings', storeId]],
    success: t('admin.subscriptions.saved'),
  });
  if (!draft || !q.data) return <Spinner label={t('common.loading')} />;
  const r = q.data.readiness;
  const dirty = JSON.stringify(draft) !== JSON.stringify(q.data.settings);
  const turningOn = draft.enabled && !q.data.settings.enabled;
  const ready = (ok: boolean, label: string) => (
    <li className="flex items-center gap-2">
      <Badge tone={ok ? 'moss' : 'amber'}>
        {ok ? t('admin.subscriptions.ok') : t('admin.subscriptions.missing')}
      </Badge>
      {label}
    </li>
  );
  return (
    <Section
      title={t('admin.subscriptions.settingsTitle')}
      description={t('admin.subscriptions.settingsLead')}
      action={<HelpTip k="subscriptions.settings" />}
    >
      <ul className="mb-5 space-y-1.5 text-sm" aria-label={t('admin.subscriptions.readiness')}>
        {ready(r.card, t('admin.subscriptions.readyCard'))}
        {ready(r.legal, t('admin.subscriptions.readyLegal'))}
        {ready(r.email, t('admin.subscriptions.readyEmail'))}
        {ready(r.products > 0, t('admin.subscriptions.readyProducts', { count: r.products }))}
      </ul>
      <fieldset disabled={!canManage} className="grid max-w-2xl gap-5">
        <legend className="sr-only">{t('admin.subscriptions.settingsTitle')}</legend>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Switch
              id="subscriptions-enabled"
              checked={draft.enabled}
              onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
              disabled={!canManage}
            />
            <label htmlFor="subscriptions-enabled" className="text-sm font-medium">
              {t('admin.subscriptions.enabled')}
            </label>
            <HelpTip k="subscriptions.enabled" />
          </div>
          <p className="text-sm text-ink-3">{t('admin.subscriptions.enabledLead')}</p>
        </div>
        <fieldset className="space-y-2">
          <legend className="flex items-center gap-1.5 text-sm font-medium">
            {t('admin.subscriptions.intervals')} <HelpTip k="subscriptions.intervals" />
          </legend>
          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {WEEKS.map((w) => (
              <label key={w} className="inline-flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-pine"
                  checked={draft.intervalsWeeks.includes(w)}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      intervalsWeeks: e.target.checked
                        ? [...draft.intervalsWeeks, w].sort((a, b) => a - b)
                        : draft.intervalsWeeks.filter((x) => x !== w),
                    })
                  }
                />
                {w === 1 ? t('admin.subscriptions.everyWeek') : t('admin.subscriptions.everyWeeks', { n: w })}
              </label>
            ))}
          </div>
          {fieldError(save.error, 'intervalsWeeks') && (
            <p className="text-sm text-rose">{fieldError(save.error, 'intervalsWeeks')}</p>
          )}
        </fieldset>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            label={t('admin.subscriptions.discount')}
            help={<HelpTip k="subscriptions.discount" />}
            description={t('admin.subscriptions.discountHint')}
            error={fieldError(save.error, 'discountBps')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                value={String(draft.discountBps / 100).replace('.', ',')}
                onChange={(e) => {
                  const n = Number(e.target.value.replace(',', '.'));
                  if (Number.isFinite(n)) setDraft({ ...draft, discountBps: Math.round(n * 100) });
                }}
              />
            )}
          </Field>
          <Field label={t('admin.subscriptions.linkHours')} help={<HelpTip k="subscriptions.link_hours" />}>
            {(p) => (
              <Select
                {...p}
                value={draft.paymentLinkHours}
                onChange={(e) => setDraft({ ...draft, paymentLinkHours: Number(e.target.value) })}
              >
                {HOURS.map((h) => (
                  <option key={h} value={h}>
                    {t('admin.subscriptions.hoursOption', { n: h })}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        {canManage && (
          <div className="flex items-center gap-2">
            {turningOn ? (
              <ConfirmButton
                variant="primary"
                size="md"
                message={t('admin.subscriptions.enableConfirm')}
                onConfirm={() => save.mutateAsync(undefined).catch(() => undefined)}
                disabled={!dirty || save.isPending}
              >
                {t('admin.subscriptions.save')}
              </ConfirmButton>
            ) : (
              <Button
                variant="primary"
                onClick={() => save.mutate(undefined)}
                disabled={!dirty || save.isPending}
              >
                {t('admin.subscriptions.save')}
              </Button>
            )}
            <HelpTip k="subscriptions.save" />
          </div>
        )}
      </fieldset>
    </Section>
  );
}

function ListSection({ storeId }: { storeId: string }) {
  const t = useT();
  const { formatDate } = useI18n();
  const money = useMoney();
  const [status, setStatus] = useState<'' | 'active' | 'paused' | 'cancelled'>('');
  const [runsOf, setRunsOf] = useState<Row | null>(null);
  const [cancelOf, setCancelOf] = useState<Row | null>(null);
  const q = useQuery({
    queryKey: ['subscriptions', storeId, status],
    queryFn: () =>
      api.get<SubscriptionList>(
        `/v1/admin/subscriptions?storeId=${storeId}${status ? `&status=${status}` : ''}`,
      ),
  });
  const tone = (s: Row['status']) => (s === 'active' ? 'moss' : s === 'paused' ? 'amber' : 'neutral');
  const day = (d: string) => formatDate(`${d}T12:00:00Z`, false);
  return (
    <Section
      title={t('admin.subscriptions.listTitle')}
      description={t('admin.subscriptions.listLead')}
      action={<HelpTip k="subscriptions.list" />}
    >
      <Field label={t('admin.subscriptions.statusFilter')} className="mb-4 max-w-xs">
        {(p) => (
          <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
            <option value="">{t('admin.subscriptions.all')}</option>
            {(['active', 'paused', 'cancelled'] as const).map((s) => (
              <option key={s} value={s}>
                {t(`admin.subscriptions.statuses.${s}`)}
              </option>
            ))}
          </Select>
        )}
      </Field>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.items.length ? (
        <EmptyState
          title={t('admin.subscriptions.emptyTitle')}
          description={t('admin.subscriptions.emptyLead')}
        />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[56rem] text-sm" aria-label={t('admin.subscriptions.listTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.subscriptions.colCustomer')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.subscriptions.colProduct')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.subscriptions.colQuantity')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.subscriptions.colInterval')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.subscriptions.colNext')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.subscriptions.colStatus')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.subscriptions.colPending')}</th>
                <th className="px-3 py-2">
                  <span className="sr-only">{t('admin.subscriptions.colActions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {q.data.items.map((r, i) => (
                <tr key={r.id} className="border-t border-line align-top">
                  <td className="px-3 py-2">
                    {r.customer ? (
                      <>
                        <Link
                          to="/musteriler/$id"
                          params={{ id: r.customer.id }}
                          className="font-medium text-pine hover:underline"
                        >
                          {r.customer.name}
                        </Link>
                        <span className="block text-ink-3">{r.customer.email}</span>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <span className="font-medium">{r.productTitle}</span>
                    <span className="block text-ink-3">
                      {[r.variantTitle, r.sku].filter(Boolean).join(' · ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-end tabular-nums">{r.quantity}</td>
                  <td className="px-3 py-2">
                    {r.intervalWeeks === 1
                      ? t('admin.subscriptions.everyWeek')
                      : t('admin.subscriptions.everyWeeks', { n: r.intervalWeeks })}
                    {r.discountBps > 0 && (
                      <span className="block text-ink-3">
                        {t('admin.subscriptions.discountShort', {
                          pct: (r.discountBps / 100).toLocaleString(),
                        })}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.status === 'cancelled' ? '—' : day(r.nextRunOn)}</td>
                  <td className="px-3 py-2">
                    <Badge tone={tone(r.status)}>{t(`admin.subscriptions.statuses.${r.status}`)}</Badge>
                    {r.reason && r.status !== 'active' && (
                      <span className="block text-ink-3">{t(`admin.subscriptions.reasons.${r.reason}`)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    {r.pending ? (
                      <>
                        <span className="tabular-nums">{r.pending.orderNumber}</span>
                        <span className="block text-ink-3">
                          {money(r.pending.totalMinor, r.pending.currency)}
                        </span>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-3 py-2 text-end">
                    <span className="inline-flex items-center gap-1">
                      <Button
                        size="sm"
                        onClick={() => setRunsOf(r)}
                        aria-label={t('admin.subscriptions.runsFor', { product: r.productTitle })}
                      >
                        {t('admin.subscriptions.runs')}
                      </Button>
                      {i === 0 && <HelpTip k="subscriptions.runs" />}
                      {r.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => setCancelOf(r)}
                          aria-label={t('admin.subscriptions.cancelFor', { product: r.productTitle })}
                        >
                          {t('admin.subscriptions.cancel')}
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {runsOf && <RunsDialog row={runsOf} onClose={() => setRunsOf(null)} />}
      {cancelOf && <CancelDialog row={cancelOf} onClose={() => setCancelOf(null)} />}
    </Section>
  );
}

function RunsDialog({ row, onClose }: { row: Row; onClose: () => void }) {
  const t = useT();
  const { formatDate } = useI18n();
  const q = useQuery({
    queryKey: ['subscription-runs', row.id],
    queryFn: () => api.get<SubscriptionRuns>(`/v1/admin/subscriptions/${row.id}/runs`),
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      variant="sheet"
      title={t('admin.subscriptions.runsTitle')}
      description={row.productTitle}
      closeLabel={t('common.close')}
    >
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data.runs.length ? (
        <p className="text-sm text-ink-2">{t('admin.subscriptions.noRuns')}</p>
      ) : (
        <ol
          className="divide-y divide-line rounded-md border border-line text-sm"
          aria-label={t('admin.subscriptions.runsTitle')}
        >
          {q.data.runs.map((r) => (
            <li key={r.runOn} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <span className="w-32 tabular-nums">{formatDate(`${r.runOn}T12:00:00Z`, false)}</span>
              <Badge tone={r.status === 'paid' ? 'moss' : r.status === 'order_created' ? 'pine' : 'neutral'}>
                {t(`admin.subscriptions.runStatuses.${r.status}`)}
              </Badge>
              {r.orderId && r.orderNumber && (
                <Link to="/siparisler/$id" params={{ id: r.orderId }} className="text-pine hover:underline">
                  {r.orderNumber}
                </Link>
              )}
            </li>
          ))}
        </ol>
      )}
    </Dialog>
  );
}

function CancelDialog({ row, onClose }: { row: Row; onClose: () => void }) {
  const t = useT();
  const [reason, setReason] = useState('');
  const m = useApiMutation(
    () => api.post(`/v1/admin/subscriptions/${row.id}/cancel`, { reason, version: row.version }),
    { invalidate: [['subscriptions']], success: t('admin.subscriptions.cancelled'), onSuccess: onClose },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.subscriptions.cancelTitle')}
      description={t('admin.subscriptions.cancelLead')}
      closeLabel={t('common.close')}
    >
      <div className="space-y-4">
        <Field
          label={t('admin.subscriptions.cancelReason')}
          help={<HelpTip k="subscriptions.cancel" />}
          error={fieldError(m.error, 'reason')}
        >
          {(p) => <Input {...p} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />}
        </Field>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            onClick={() => m.mutate(undefined)}
            disabled={m.isPending || reason.trim().length < 3}
          >
            {t('admin.subscriptions.cancelSubmit')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
